import { forwardRef } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import { Text } from '@buffer-mono/popcorn'

import type { PostCard_Post } from '~publish/components/PostCard'
import {
  PostCardBody,
  PostCardContainer,
  PostCardFrame,
  PostCardProvider,
} from '~publish/components/PostCard'
import { usePostData } from '~publish/components/PostCard/PostCardContext'
import { PostCardTagsCompact } from '~publish/components/PostCardTags'
import type { FragmentOf } from '~publish/graphql'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'

import postCardStyles from '~publish/components/PostCard/PostCard.module.css'
import styles from './BoardCardHoverMenu.module.css'

import { BoardPostCardActions } from './BoardPostCardActions'

/**
 * The card's own sort key is `dueAt ?? sentAt` (see
 * `BoardColumnContainer.sortKeyForPost`) — showing that same date is what
 * makes a column's order legible.
 */
function BoardPostCardDate(): JSX.Element | null {
  const post = usePostData()
  const formatDateTime = useDateTimeFormatter()
  const date = post.dueAt ?? post.sentAt
  if (!date) return null

  return (
    <Text size="sm" color="subtle" className={styles.date}>
      {formatDateTime(date, { includeTimeZone: false })}
    </Text>
  )
}

/**
 * Board's card, mirroring `PostCardCompact` (content only, no footer/metrics)
 * plus a hover-revealed "More" menu in place of the full inline action set —
 * the same interaction Ideas' board card uses, since a dense Kanban column
 * has no room for a per-status action row.
 */
export const BoardPostCard = forwardRef<
  HTMLDivElement,
  { post: FragmentOf<typeof PostCard_Post>; className?: string }
>(({ post, className }, ref) => {
  return (
    <PostCardProvider post={post}>
      <PostCardContainer
        className={clsx(
          postCardStyles['rounded-sm'],
          styles.container,
          className,
        )}
      >
        <PostCardFrame ref={ref}>
          <div className={postCardStyles.inner}>
            <BoardPostCardDate />
            <PostCardBody tags={<PostCardTagsCompact />} />
          </div>
          <div className={styles.menu}>
            <BoardPostCardActions />
          </div>
        </PostCardFrame>
      </PostCardContainer>
    </PostCardProvider>
  )
})

BoardPostCard.displayName = 'BoardPostCard'
